import { ref, onMounted, watch, nextTick } from 'vue';
import type { FilterConfiguration } from '@/components/base/dynamicFilterBuilder';
import { useI18n } from '@/composables/useI18n';
import { isAbortError, useAbortController } from "@/composables/useAbortController";
import { fetchCustomers, DEFAULT_SORT_COLUMN_FETCH_CUSTOMERS, loadCustomerFromStorageOrFirst } from '@/components/customers/scripts';
import type { Customer, FetchCustomersParam } from '@/components/customers/scripts';

/**
 * Options for configuring the customer filter.
 */
interface CustomerFilterOptions {
  /**
   * Callback function to be called when the customer is initialized after the default customer is loaded.
   * @param customer - The initialized customer or null.
   */
  onInitialized?: (customer: Customer | null) => void;

  /**
   * Callback function to be called when the customer filter is reloaded.
   * Customer filter reload requires initializeWithCustomerFromStorage to be true
   * @param customer - The reloaded customer or null.
   */
  onReload?: (customer: Customer | null) => void;

  /**
   * Callback function to be called when an error occurs.
   * @param error - The error object.
   */
  onError?: (error: unknown) => void;

  /**
   * Whether to initialize the customer filter with the customer saved in local storage or the first customer.
   * If false or undefined, the filter will start empty.
   */
  initializeWithCustomerFromStorage?: boolean;

  /** Initialize only when entityId resolves to an ID; never fall back to storage or the first customer. */
  initializeWithEntityIdOnly?: boolean;

  /**
   * Optional entity ID from a route param. When provided and > 0, overrides localStorage lookup
   * and loads the customer directly by this ID.
   * Can be a plain value or a getter function for reactive use (e.g., from route.params.id).
   */
  entityId?: (() => number | null) | number | null;

  /**
   * Whether to preload the customer list on mount by calling getFilteredItems with no search input.
   * Useful when initializeWithCustomerFromStorage is false but you still want items ready in the dropdown.
   */
  preloadItems?: boolean;
}

/**
 * Composable for creating and managing a customer filter.
 * The filter component can be initialized with the default customer from storage or the first customer found on the server.
 * The filter configuration needs to be used with the DynamicFilterBuilder component.
 * @param options - Optional configuration options for the customer filter.
 * @returns An object containing the customer filter configuration and the selected customer.
 */
export const useCustomerFilter = (options?: CustomerFilterOptions) => {
  const { t } = useI18n();
  const { getNewAbortController } = useAbortController();
  const selectedCustomer = ref<Customer | null>(null); // model used to initialize the value of the filter
  const preloadRefreshTrigger = ref(0);

  const resolveEntityId = (): number | null | undefined => {
    if (typeof options?.entityId === 'function') return options.entityId();
    return options?.entityId ?? undefined;
  };

  const getFilteredItems = async (searchInput: string | null) => {
    const fetchArgs: FetchCustomersParam = {
      sortColumn: DEFAULT_SORT_COLUMN_FETCH_CUSTOMERS
    };
    if (searchInput) fetchArgs.companyName = searchInput;

    const { error, response } = await fetchCustomers(fetchArgs, getNewAbortController().signal);

    if (error) {
      options?.onError?.(error);
    }

    return response || [];
  }

  /**
   * Creates a filter configuration for selecting customers.
   * @param config - Configuration for the filter including column properties, events, and model.
   * @returns The filter configuration object.
   */
  const createCustomerFilterConfiguration = (
    config?: Pick<FilterConfiguration, 'colProps' | 'events' | "debounce" | "props">
  ): FilterConfiguration => {
    return {
      ...(config ?? {}),
      type: "combobox",
      props: {
        label: t("CustomerApiKeyManagement.customer_selection_field"),
        "item-text": "companyName",
        placeholder: t("CustomerApiKeyManagement.customer_selection_field"),
        ...config?.props ?? {},
      },
      model: selectedCustomer,
      getFilteredItems: getFilteredItems,
      ...(options?.preloadItems ? { refreshTrigger: preloadRefreshTrigger } : {}),
    };
  };

  onMounted(async () => {
    if (options?.initializeWithEntityIdOnly === true && resolveEntityId() != null) {
      try {
        const { response, error } = await loadCustomerFromStorageOrFirst(resolveEntityId() ?? undefined);
        if (error) {
          options?.onError?.(error);
          return;
        }
        if (response) {
          selectedCustomer.value = response;
          options?.onInitialized?.(response);
        }
      } catch (error) {
        if (!isAbortError(error)) options?.onError?.(error);
      }
    } else if (options?.initializeWithCustomerFromStorage === true) {
      try {
        const { response, error } = await loadCustomerFromStorageOrFirst(resolveEntityId() ?? undefined);
        if (error) {
          options?.onError?.(error);
          return;
        }
        if (response) {
          // set initial value
          selectedCustomer.value = response;
          options?.onInitialized?.(response);
        }
      } catch (error) {
        if (!isAbortError(error)) {
          options?.onError?.(error);
        }
      }
    }

    if (options?.preloadItems) {
      // nextTick ensures DynamicFilterBuilder has mounted and set up its refreshTrigger watch
      await nextTick();
      preloadRefreshTrigger.value++;
    }
  });

  const triggerFilterReload = async () => {
    if (options?.initializeWithCustomerFromStorage === true) {
      try {
        const { response, error } = await loadCustomerFromStorageOrFirst(resolveEntityId() ?? undefined);
        if (error) {
          options?.onError?.(error);
          return;
        }
        if (response) {
          // set reload value
          selectedCustomer.value = response;
          options?.onReload?.(response);
        }
      } catch (error) {
        if (!isAbortError(error)) {
          options?.onError?.(error);
        }
      }
    } else {
      console.warn("Customer filter reload requires initializeWithCustomerFromStorage to be true. Reload skipped.");
    }
  }

  // Watch for reactive entityId changes (e.g., from route.params.id via computed)
  if (typeof options?.entityId === 'function') {
    watch(options.entityId, async (newId, oldId) => {
      if (newId === oldId) return;
      // Skip if the currently displayed entity already matches the new ID.
      // This happens when the URL changed as a result of the user's own combobox selection.
      if (newId != null && selectedCustomer.value?.id === newId) return;
      if (options?.initializeWithCustomerFromStorage !== true && options?.initializeWithEntityIdOnly !== true) return;
      if (newId == null) {
        selectedCustomer.value = null;
        return;
      }
      try {
        const { response, error } = await loadCustomerFromStorageOrFirst(resolveEntityId() ?? undefined);
        if (error) { options?.onError?.(error); return; }
        if (response) {
          selectedCustomer.value = response;
          // Prefer onReload; fall back to onInitialized for components that only define onInitialized
          if (options?.onReload) {
            options.onReload(response);
          } else {
            options?.onInitialized?.(response);
          }
        }
      } catch (error) {
        if (!isAbortError(error)) options?.onError?.(error);
      }
    });
  }

  const clearFilter = () => {
    selectedCustomer.value = null;
  };

  return {
    createCustomerFilterConfiguration,
    triggerFilterReload,
    clearFilter,
  };
};
